import { DrawerCloseButton, SharedForm } from '@components';
import { CreateUnavailability } from '@services';
import { useStore } from '@stores';
import { Button, Col, DatePicker, Drawer, Form, FormInstance, Input, Row, Typography } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { UnavailabilityTable } from './UnavailabilityTable';

const { Text } = Typography;

interface IProps {
  form: FormInstance<any>;
  name?: string;
  userId?: number;
  workStationId?: string;
  setClose: () => void;
  handleSubmit: (values: CreateUnavailability) => Promise<void>;
}

const UnavailabilityDrawer: React.FC<IProps> = ({
  form,
  name,
  userId,
  workStationId,
  setClose,
  handleSubmit,
}) => {
  const { unavailabilityStore } = useStore();
  const title = workStationId
    ? 'Add workstation unavailability'
    : userId
      ? 'Add staff unavailability'
      : 'Add non working days';

  const description = workStationId ? (
    <>
      Below you can select the dates on which a workstation will not be available.
      <br />
      All assigned job cards for those days will be unassigned, remember to reach out to the
      customers if needed.
    </>
  ) : userId ? (
    <>
      Below you can select the dates on which a team member will not be available.
      <br />
      All assigned job cards for those days will be unassigned, remember to reach out to the
      customers if needed.
    </>
  ) : (
    <>
      Below you can select the dates the garage is not working.
      <br />
      All assigned job cards for those days will be unassigned, remember to reach out to the
      customers if needed.
    </>
  );

  return (
    <Drawer
      open={true}
      styles={{ wrapper: { width: 800 } }}
      onClose={setClose}
      title={`Manage ${name} unavailability`}
      placement="right"
      closeIcon={false}
      extra={<DrawerCloseButton onClick={setClose} />}
    >
      <Row>
        <Col>
          <Text strong>{title}</Text>
        </Col>
      </Row>
      <Row>
        <Col>
          <Text>{description}</Text>
        </Col>
      </Row>
      <br />
      <SharedForm hideButtons form={form} onFinish={handleSubmit}>
        <Row gutter={8}>
          <Col hidden>
            <Form.Item name="workStationId" required>
              <Input />
            </Form.Item>
          </Col>
          <Col hidden>
            <Form.Item name="userId" required>
              <Input />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              required
              name="name"
              rules={[{ required: true, message: 'Please enter a name' }]}
            >
              <Input placeholder="Description" />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              required
              name="startAt"
              rules={[{ required: true, message: 'Please select a start date' }]}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                placeholder="Select start date"
                disabledDate={(current) => current < dayjs().startOf('day')}
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              required
              name="endAt"
              dependencies={['startAt']}
              rules={[
                { required: true, message: 'Please select an end date' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startAt = getFieldValue('startAt');
                    if (
                      !value ||
                      !startAt ||
                      value.isSame(startAt, 'day') ||
                      value.isAfter(startAt, 'day')
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('End date must be the same date or after the start date')
                    );
                  },
                }),
              ]}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                placeholder="Select end date"
                disabledDate={(current) => {
                  const startAt = form.getFieldValue('startAt');
                  return (
                    current < dayjs().startOf('day') ||
                    (startAt && current < dayjs(startAt).startOf('day'))
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={unavailabilityStore.loading}
                disabled={unavailabilityStore.loading}
              >
                Confirm
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </SharedForm>
      <Row>
        <Col>
          <Text>Scheduled non working days</Text>
        </Col>
      </Row>
      <Row>
        <Col>
          <UnavailabilityTable userId={userId} workStationId={workStationId} />
        </Col>
      </Row>
    </Drawer>
  );
};

export default observer(UnavailabilityDrawer);
